import { Message } from '@arco-design/web-react';
import {
  AbortMultipartUploadCommand,
  CompleteMultipartUploadCommand,
  CreateMultipartUploadCommand,
  type ObjectCannedACL,
  S3Client,
  UploadPartCommand,
} from '@aws-sdk/client-s3';

import i18n from '@/i18n';
import { getUploadFileToken, getUploadImgToken, getUploadImgTokenGlobal } from '@/service/token';

import { getUserInfo } from './useLocalStorage';

// ============================================================
// 类型定义
// ============================================================

/**
 * 后端 API 返回的 S3 Token 结构
 * 注意："ctedentials" 是后端 API 的拼写错误，保留以兼容
 */
export interface S3Token {
  url: string;
  bucket: string;
  ctedentials: {
    Source?: string;
    AccessKeyID: string;
    SecretAccessKey: string;
    SessionToken?: string;
    Expires: string;
  };
}

/** 获取 S3 临时凭证 */
export type S3TokenFetcher = (username: string) => Promise<S3Token>;

/**
 * 分片上传选项
 */
export interface MultipartUploadOptions {
  file: Blob;
  bucket: string;
  key: string;
  onProgress?: (percent: number) => void;
  abortSignal?: AbortSignal;
  ACL?: ObjectCannedACL;
  partSize?: number;
  concurrency?: number;
}

/**
 * S3 上传操作结果
 */
export interface S3UploadResult {
  etag: any;
  size: number;
  bucket: string;
  key: string;
  location?: string;
}

/**
 * uploadToS3 主函数选项
 */
export interface UploadToS3Options {
  file: Blob;
  key: string;
  onProgress?: (percent: number) => void;
  abortController?: AbortController;
  ACL?: ObjectCannedACL;
  concurrency?: number;
  partSize?: number;
  /** 默认镜像 STS；产品图可传 getUploadImgToken / getUploadImgTokenGlobal */
  getToken?: S3TokenFetcher;
}

/** 带业务会话的上传参数（制品版本 / 产品图等） */
export interface SessionUploadParams {
  id: string;
  file: { originFile: File; name?: string; uid?: string; [key: string]: any };
  key: string;
  form?: any;
  module: string;
  onProgress?: ((evt: { percent: number; id: string; key: string }) => void) | null;
  onSuccess?: ((res: { etag: any; obj: any; id: string; size: number; bucket: string; key: string }) => void | Promise<void>) | null;
}

// ============================================================
// 内部状态与缓存
// ============================================================

let s3ClientCache: S3Client | null = null;
let currentToken: S3Token | null = null;
let currentTokenFetcher: S3TokenFetcher | null = null;

/** 按 module/key 跟踪进行中的上传，供取消与进度查询 */
const uploadMap: Record<string, Record<string, any>> = {};

/**
 * 检查 token 是否已过期
 */
function isTokenExpired(token: S3Token | null): boolean {
  if (!token?.ctedentials?.Expires) return true;
  return new Date(token.ctedentials.Expires).getTime() < Date.now();
}

/**
 * 创建或获取缓存的 S3 客户端
 * 如果 token 无效则抛出错误（不递归）
 */
export async function createS3Client(getToken: S3TokenFetcher = getUploadFileToken as S3TokenFetcher): Promise<S3Client> {
  // 同一凭证源且 token 仍有效时复用客户端
  if (s3ClientCache && currentToken && currentTokenFetcher === getToken && !isTokenExpired(currentToken)) {
    return s3ClientCache;
  }

  currentTokenFetcher = getToken;
  currentToken = (await getToken(getUserInfo()?.name)) as any;
  if (!currentToken?.bucket) {
    throw new Error('Invalid token: missing bucket');
  }

  s3ClientCache = new S3Client({
    region: currentToken.ctedentials?.Source || 'cn-beijing',
    endpoint: currentToken.url,
    forcePathStyle: true,
    credentials: {
      accessKeyId: currentToken.ctedentials.AccessKeyID,
      secretAccessKey: currentToken.ctedentials.SecretAccessKey,
      sessionToken: currentToken.ctedentials?.SessionToken,
      expiration: new Date(currentToken.ctedentials.Expires),
    },
  });

  return s3ClientCache;
}

/**
 * 清除缓存的 S3 客户端和 token
 * 用于测试或强制刷新
 */
export function clearS3ClientCache(): void {
  s3ClientCache = null;
  currentToken = null;
  currentTokenFetcher = null;
}

// ============================================================
// 分片上传并发控制
// ============================================================

/**
 * 使用受控的并发量上传分片
 * 使用简单的队列模式限制并行上传数量
 */
async function uploadPartsWithConcurrency({
  client,
  file,
  bucket,
  key,
  uploadId,
  partSize,
  onProgress,
  abortSignal,
  concurrency = 3,
}: {
  client: S3Client;
  file: Blob;
  bucket: string;
  key: string;
  uploadId: string;
  partSize: number;
  onProgress?: (percent: number) => void;
  abortSignal?: AbortSignal;
  concurrency?: number;
}): Promise<{ ETag: string | undefined; PartNumber: number }[]> {
  const fileSize = file.size;
  const partCount = Math.ceil(fileSize / partSize);
  let completedCount = 0;
  const parts = new Array(partCount).fill(null);

  const queue: Promise<void>[] = [];
  let running = 0;

  for (let partNumber = 1; partNumber <= partCount; partNumber++) {
    // 开始每个分片前检查是否中止
    if (abortSignal?.aborted) {
      throw new Error('Upload aborted');
    }

    const uploadPart = async () => {
      const start = (partNumber - 1) * partSize;
      const end = Math.min(start + partSize, fileSize);
      const blobPart = file.slice(start, end);
      const arrayBuffer = await blobPart.arrayBuffer();

      const result = await client.send(
        new UploadPartCommand({
          Bucket: bucket,
          Key: key,
          PartNumber: partNumber,
          UploadId: uploadId,
          Body: new Uint8Array(arrayBuffer),
        }),
        { abortSignal },
      );

      parts[partNumber - 1] = {
        ETag: result.ETag,
        PartNumber: partNumber,
      };

      completedCount++;
      if (onProgress) {
        onProgress(Math.round((completedCount / partCount) * 100));
      }
    };

    // 并发控制：达到上限时等待
    if (running >= concurrency) {
      await Promise.race(queue);
    }

    const promise = uploadPart()
      .catch((err) => {
        // 从队列中移除失败的 promise
        const idx = queue.indexOf(promise as any);
        if (idx > -1) queue.splice(idx, 1);
        throw err;
      })
      .then(() => {
        // 从队列中移除已完成的 promise
        const idx = queue.indexOf(promise as any);
        if (idx > -1) queue.splice(idx, 1);
        running--;
      });

    queue.push(promise);
    running++;
  }

  // 等待所有剩余上传完成
  await Promise.all(queue);

  return parts.filter((p) => p != null);
}

/**
 * 支持中止的分片上传实现
 */
async function multipartUpload({
  client,
  file,
  bucket,
  key,
  onProgress,
  abortSignal,
  ACL = 'public-read',
  partSize = 5 * 1024 * 1024,
  concurrency = 3,
}: MultipartUploadOptions & { client: S3Client }): Promise<{
  ETag?: string;
  Location?: string;
}> {
  // 1. 创建分片上传
  const createRes = await client.send(
    new CreateMultipartUploadCommand({
      Bucket: bucket,
      Key: key,
      ACL,
    }),
  );

  const uploadId = createRes.UploadId;
  if (!uploadId) {
    throw new Error('Failed to create multipart upload: missing UploadId');
  }

  try {
    // 2. 并发上传分片
    const parts = await uploadPartsWithConcurrency({
      client,
      file,
      bucket,
      key,
      uploadId,
      partSize,
      onProgress,
      abortSignal,
      concurrency,
    });

    // 3. 完成分片上传
    const completeRes = await client.send(
      new CompleteMultipartUploadCommand({
        Bucket: bucket,
        Key: key,
        UploadId: uploadId,
        MultipartUpload: { Parts: parts },
      }),
    );

    return completeRes;
  } catch (err) {
    // 4. 失败时中止分片上传
    try {
      await client.send(
        new AbortMultipartUploadCommand({
          Bucket: bucket,
          Key: key,
          UploadId: uploadId,
        }),
      );
    } catch (abortErr) {
      // 清理期间忽略中止错误
      console.warn('Failed to abort multipart upload:', abortErr);
    }
    throw err;
  }
}

// ============================================================
// 主上传函数
// ============================================================

/**
 * 上传文件到 S3，支持自动 token 管理和并发分片上传
 *
 * @param options - 上传选项，包括文件、key、token 获取器、回调函数
 * @returns Promise，包含上传结果（etag、size、bucket、key）
 *
 * @example
 * ```ts
 * const result = await uploadToS3({
 *   file: fileBlob,
 *   key: 'uploads/my-file.bin',
 *   onProgress: (percent) => console.log(`${percent}%`),
 *   abortController: new AbortController(),
 *   concurrency: 5,
 * });
 * ```
 */
export async function uploadToS3<T extends Record<string, any> = S3UploadResult>(options: UploadToS3Options): Promise<T> {
  const {
    file,
    key,
    onProgress,
    abortController,
    ACL = 'public-read',
    concurrency = 3,
    partSize = 5 * 1024 * 1024,
    getToken = getUploadFileToken as S3TokenFetcher,
  } = options;

  const client = await createS3Client(getToken);

  if (!currentToken?.bucket) {
    throw new Error('Cannot upload: bucket not available');
  }

  const result = await multipartUpload({
    client,
    file,
    bucket: currentToken.bucket,
    key,
    onProgress,
    abortSignal: abortController?.signal,
    ACL,
    partSize,
    concurrency,
  });

  const baseResult = {
    etag: result.ETag ? JSON.parse(result.ETag) : null,
    size: file.size,
    bucket: currentToken.bucket,
    key,
    location: result.Location,
  };
  return baseResult as unknown as T;
}

/**
 * 会话式上传：写入 uploadMap，统一进度/取消，底层走 uploadToS3
 */
async function uploadWithSession(
  params: SessionUploadParams,
  getToken: S3TokenFetcher,
  successMessageKey: string,
  callback: () => void = () => {},
) {
  const t = i18n.t.bind(i18n);
  const { id, file, key, form, module, onProgress = null, onSuccess = null } = params;
  const abortController = new AbortController();

  try {
    const { originFile } = file;
    uploadMap[module] = uploadMap[module] || {};
    uploadMap[module][key] = {
      originFile,
      form,
      status: 'uploading',
      percent: 0,
      key,
      id,
      formation: originFile?.name?.endsWith('img') ? 'raw' : 'qcow2',
      abortController,
    };
    const currObj = uploadMap[module][key];

    const result = await uploadToS3({
      file: originFile,
      key,
      getToken,
      abortController,
      onProgress: (percent) => {
        currObj.percent = percent;
        onProgress?.({ percent, id: currObj.id, key: currObj.key });
      },
    });

    Message.success(t(successMessageKey));
    currObj.status = 'success';
    currObj.percent = 100;
    if (onSuccess) {
      await onSuccess({
        etag: result.etag,
        obj: currObj,
        id,
        size: originFile.size,
        bucket: result.bucket,
        key,
      });
    }
    delete uploadMap?.[module]?.[key];
    onProgress?.({ id, key, percent: 100 });
    callback();
  } catch (e: any) {
    console.error('upload fail', e);
    let msg = 'Upload failed';
    if (e?.message || e?.Message) {
      msg = e?.message || e?.Message;
    } else if (e?.response?.data?.message) {
      msg = e?.response?.data?.message;
    } else if (typeof e === 'string') {
      msg = e;
    }
    if (String(msg).includes('aborted')) {
      Message.error(t('common:aborted.upload'));
    } else {
      Message.error(msg);
    }
    delete uploadMap?.[module]?.[key];
  }
}

/** 制品等文件上传（镜像 STS） */
export function uploadFile(params: SessionUploadParams, callback?: () => void) {
  return uploadWithSession(params, getUploadFileToken as S3TokenFetcher, 'common:success.upload', callback);
}

/** 产品图等图片上传（ophub S3 凭证，数据中心） */
export function uploadImg(params: SessionUploadParams, callback?: () => void) {
  return uploadWithSession(params, getUploadImgToken as S3TokenFetcher, 'success.upload', callback);
}

/** 系统运营中心产品类别图等（ophub S3 凭证，全局） */
export function uploadImgGlobal(params: SessionUploadParams, callback?: () => void) {
  return uploadWithSession(params, getUploadImgTokenGlobal as S3TokenFetcher, 'success.upload', callback);
}

/** 按 module/key 取消会话上传 */
export function abortUpload(module: string, key: string) {
  const obj = uploadMap?.[module]?.[key];
  if (obj?.abortController) {
    obj.abortController.abort();
    delete uploadMap?.[module]?.[key];
  }
}

export const getUploadObjects = (module: string) => uploadMap?.[module] || {};

// ============================================================
// 工具函数
// ============================================================

/**
 * 通过触发 AbortController 中止正在进行的 S3 上传
 */
export function abortS3Upload(abortController: AbortController | null | undefined): void {
  if (abortController) {
    abortController.abort();
  }
}

/**
 * 将字节数格式化为人类可读字符串
 * @param bytes - 字节数
 * @param decimals - 小数位数（默认：2）
 * @returns 格式化后的字符串，如 "1.5 Mi"
 */
export function formatBytes(bytes: number, decimals = 2): string {
  if (String(bytes).includes('i')) return bytes ? String(bytes).replace(' ', '') : '';
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'Ki', 'Mi', 'Gi', 'Ti'];
  let i = Math.max(0, Math.round(Math.log(bytes) / Math.log(k)));
  let res = parseFloat((bytes / Math.pow(k, i)).toFixed(dm));

  while (true) {
    if (res > 1 || i < 2) break;
    res = res * k;
    i--;
  }

  return `${parseInt(String(res), 10)}${sizes[i]}`;
}
